import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { UsePaginationState } from '@uhg-abyss/web/hooks/usePagination';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { isNil } from 'lodash';
import React, { ReactNode } from 'react';

import { ResultsCount } from '../ResultsCount';
import { CompactPagination } from './components/CompactPagination';
import { GoToPage } from './components/GoToPage';
import { ResultsPerPage } from './components/ResultsPerPage';
import { Styles } from './includes/styles';

type HeaderProps = {
  headerBottom?: ReactNode;
  headerLeft?: ReactNode;
  headerRight?: ReactNode;
  headerTop?: ReactNode;
  pageSizeOptions?: number[];
  pagination: UsePaginationState;
  resultsPerPage?: number;
  setResultsPerPage?: (size: number) => void;
  showCompactPagination?: boolean;
  showGoToPage?: boolean;
  showResultsCount?: boolean;
  showResultsPerPage?: boolean;
  totalResults?: number;
};

/**
 * Header
 *
 * Displays components above the table which are used to control the data within the table.
 */
export function Header({
  headerBottom = null,
  headerLeft = null,
  headerRight = null,
  headerTop = null,
  pageSizeOptions = [25, 50, 100, 200],
  pagination = {
    canNextPage: false,
    canPreviousPage: false,
    firstPage(): void {
      throw new Error('Function not implemented.');
    },
    goToPage(pageNumber: number): void {
      throw new Error('Function not implemented.');
    },
    gotoPage(pageNumber: number): void {
      throw new Error('Function not implemented.');
    },
    lastPage(): void {
      throw new Error('Function not implemented.');
    },
    nextPage(): void {
      throw new Error('Function not implemented.');
    },
    pageCount: 0,
    pageIndex: 0,
    previousPage(): void {
      throw new Error('Function not implemented.');
    },
    setData(value: React.SetStateAction<object[]>): void {
      throw new Error('Function not implemented.');
    },
    state: {
      currentPage: 0,
      pageCount: 0,
      pageIndex: 0,
      rowCount: 0,
      rows: [],
    },
  },
  resultsPerPage = 25,
  setResultsPerPage = () => {},
  showCompactPagination = false,
  showGoToPage = false,
  showResultsCount = false,
  showResultsPerPage = false,
  totalResults = 0,
}: HeaderProps) {
  return (
    <ErrorHandler location="src/shared/ui/components/TableQuery/components/Header/Header.jsx">
      <Styles>
        <Grid className="tableHeader">
          {!isNil(headerTop) && (
            <Grid.Col className="top" span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
              {headerTop}
            </Grid.Col>
          )}
          <Grid.Col className="left" span={{ lg: '50%', md: '50%', sm: '100%', xs: '100%' }}>
            {!isNil(headerLeft) && headerLeft}
          </Grid.Col>
          <Grid.Col className="right" span={{ lg: '50%', md: '50%', sm: '100%', xs: '100%' }}>
            {!isNil(headerRight) ? (
              headerRight
            ) : (
              <React.Fragment>
                <Flex
                  alignContent="flex-start"
                  alignItems="center"
                  className="topRow"
                  direction="row"
                  justify="flex-end"
                >
                  {showResultsPerPage && (
                    <ResultsPerPage
                      pageSizeOptions={pageSizeOptions}
                      resultsPerPage={resultsPerPage}
                      setResultsPerPage={setResultsPerPage}
                    />
                  )}
                  {showCompactPagination && <CompactPagination pagination={pagination} />}
                  {showGoToPage && (
                    <GoToPage
                      currentPage={pagination.state.currentPage}
                      navigate={pagination?.gotoPage}
                      totalPages={pagination?.pageCount}
                    />
                  )}
                </Flex>
                <Flex
                  alignContent="flex-start"
                  alignItems="center"
                  className="bottomRow"
                  direction="row"
                  justify="flex-end"
                >
                  {showResultsCount && (
                    <ResultsCount
                      currentPage={pagination.state.pageIndex}
                      resultsPerPage={resultsPerPage}
                      totalResults={totalResults}
                    />
                  )}
                </Flex>
              </React.Fragment>
            )}
          </Grid.Col>
          {!isNil(headerBottom) && (
            <Grid.Col className="bottom" span={{ lg: '100%', md: '100%', sm: '100%', xs: '100%' }}>
              {headerBottom}
            </Grid.Col>
          )}
        </Grid>
      </Styles>
    </ErrorHandler>
  );
}
